// shared/payoffSimulation.ts
// Canonical home for the credit-card payoff simulation math.
// Imported by both the app (`TrueCost/lib/financialHelpers.ts` re-exports
// these) and the marketing website's public payoff calculators.
//
// Pure TypeScript, no dependencies. Do NOT add imports that resolve from
// node_modules — this module must be importable from any JS/TS environment.
//
// The math here is the canonical implementation. Do not duplicate or
// re-derive it elsewhere; both app and website must import from this file.

/**
 * Round to 2 decimal places. Inlined here so the module stays self-contained
 * (no cross-package helper imports).
 */
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

// ─── Daily-compounding credit-card math ──────────────────────────────────────
//
// Credit cards compound interest daily (APR / 365), not monthly. The helpers
// below surface the "real" effective rates so callers can explain them to the
// user AND so payoff / interest-avoided calculations match what the bank
// actually charges.
//
// Conventions:
// - `apr` is the nominal APR as a decimal (e.g. 0.24 for 24%)
// - 30-day month approximation is used for the monthly rate
// - All returns are decimals, not percentages
//
// Rates are displayed to users via `effectiveAnnualRate()` in the "interest
// avoided" info modal so they understand where the number comes from.

/**
 * Effective monthly rate from daily-compounded APR, 30-day month.
 * `(1 + apr/365)^30 − 1`
 */
export function monthlyRateFromDailyCompounding(apr: number): number {
  if (apr <= 0) return 0;
  return Math.pow(1 + apr / 365, 30) - 1;
}

/**
 * Effective annual rate (EAR) from daily-compounded APR.
 * `(1 + apr/365)^365 − 1`
 * e.g. APR 0.20 → EAR ≈ 0.2213 (22.13%)
 */
export function effectiveAnnualRate(apr: number): number {
  if (apr <= 0) return 0;
  return Math.pow(1 + apr / 365, 365) - 1;
}

// ─── Month-by-month payoff simulation ────────────────────────────────────────

export interface SimulationMonth {
  month: number;
  openingBalance: number;
  interestAccrued: number;
  paymentApplied: number;
  closingBalance: number;
}

export interface SimulationResult {
  months: SimulationMonth[];
  totalInterest: number;
  endBalance: number;
  isPaidOff: boolean;
  /** Month number when balance first hit 0 (1-indexed). Null if not paid off within sim window. */
  monthsToPayoff: number | null;
  /**
   * Shadow interest: total interest that would have accrued at a shadow APR
   * (e.g. post-promo rate) on the same balance trajectory. Used for deferred-
   * interest promo calculations. Zero unless `shadowApr` was passed to
   * `simulateMinimumPayoff`.
   */
  shadowInterest: number;
}

export interface SimulatePayoffParams {
  balance: number;
  apr: number;
  monthlyPayment: number;
  /** Max months to simulate. */
  months: number;
  /** If true, stop iterating once balance hits 0. Default true. */
  stopOnPaidOff?: boolean;
}

/**
 * Simulate debt payoff month-by-month.
 *
 * Each month:
 *   interest = openingBalance × monthlyRate
 *   payment  = min(monthlyPayment, openingBalance + interest)
 *   closing  = openingBalance + interest − payment
 *
 * Uses daily-compounded monthly rate. Caps the payment at the amount owed so
 * we don't report negative closing balances.
 */
export function simulatePayoff(params: SimulatePayoffParams): SimulationResult {
  const { balance: startBalance, apr, monthlyPayment, months, stopOnPaidOff = true } = params;
  const rate = monthlyRateFromDailyCompounding(apr);

  if (startBalance <= 0) {
    return { months: [], totalInterest: 0, endBalance: 0, isPaidOff: true, monthsToPayoff: 0, shadowInterest: 0 };
  }

  let balance = startBalance;
  let totalInterest = 0;
  const log: SimulationMonth[] = [];
  let paidOffMonth: number | null = null;

  for (let m = 1; m <= months; m++) {
    if (balance <= 0) break;
    const openingBalance = balance;
    const interest = openingBalance * rate;
    const due = openingBalance + interest;
    const payment = Math.min(Math.max(0, monthlyPayment), due);
    const closing = Math.max(0, due - payment);

    log.push({
      month: m,
      openingBalance: round2(openingBalance),
      interestAccrued: round2(interest),
      paymentApplied: round2(payment),
      closingBalance: round2(closing),
    });

    totalInterest += interest;
    balance = closing;

    if (stopOnPaidOff && balance <= 0.005) {
      paidOffMonth = m;
      balance = 0;
      break;
    }
  }

  return {
    months: log,
    totalInterest: round2(totalInterest),
    endBalance: round2(balance),
    isPaidOff: balance <= 0.005,
    monthsToPayoff: paidOffMonth,
    shadowInterest: 0,
  };
}

export interface SimulateMinimumParams {
  balance: number;
  apr: number;
  /** Minimum payment floor in dollars. Default 25. */
  minFloor?: number;
  /** Minimum payment as pct of balance, decimal. Default 0.02. */
  minPct?: number;
  /** Max iterations (months). Default 1200 (100 years). */
  months?: number;
  /**
   * Optional shadow APR (decimal, e.g. 0.24 for 24%). When provided, the
   * simulation also accumulates interest at this rate on the same balance
   * trajectory. The result is returned as `shadowInterest` and models the
   * retroactive interest charge used in deferred-interest promotions.
   */
  shadowApr?: number;
}

/**
 * Simulate debt payoff using CARD-Act-style shrinking minimum payments.
 *
 * Unlike `simulatePayoff` (which uses a fixed monthly payment), this function
 * re-computes the minimum payment each month from the *current* balance:
 *   payment = max(minFloor, balance × minPct)
 *
 * This accurately models the "minimum payment trap" where shrinking payments
 * extend payoff dramatically compared to a fixed starting minimum.
 *
 * For a $3,000 balance at 22% APR (2% floor $25) expect ~630+ months to payoff.
 */
export function simulateMinimumPayoff(params: SimulateMinimumParams): SimulationResult {
  const {
    balance: startBalance,
    apr,
    minFloor = 25,
    minPct = 0.02,
    months: maxMonths = 1200,
    shadowApr,
  } = params;

  if (startBalance <= 0) {
    return { months: [], totalInterest: 0, endBalance: 0, isPaidOff: true, monthsToPayoff: 0, shadowInterest: 0 };
  }

  const rate = monthlyRateFromDailyCompounding(apr);
  const shadowRate = shadowApr !== undefined ? monthlyRateFromDailyCompounding(shadowApr) : 0;
  let balance = startBalance;
  let totalInterest = 0;
  let shadowInterestTotal = 0;
  const log: SimulationMonth[] = [];
  let paidOffMonth: number | null = null;

  for (let m = 1; m <= maxMonths; m++) {
    if (balance <= 0) break;

    const openingBalance = balance;
    const interest = openingBalance * rate;
    const due = openingBalance + interest;

    // Shadow interest accrues at post-promo rate on the same opening balance
    // (CFPB average-daily-balance method for deferred-interest offers).
    if (shadowRate > 0) {
      shadowInterestTotal += openingBalance * shadowRate;
    }

    // Minimum payment re-computed from current (opening) balance each month.
    const minPayment = Math.max(minFloor, openingBalance * minPct);
    // Cap payment at total amount due so closing balance never goes negative.
    const payment = Math.min(minPayment, due);
    const closing = Math.max(0, due - payment);

    log.push({
      month: m,
      openingBalance: round2(openingBalance),
      interestAccrued: round2(interest),
      paymentApplied: round2(payment),
      closingBalance: round2(closing),
    });

    totalInterest += interest;
    balance = closing;

    if (balance <= 0.005) {
      paidOffMonth = m;
      balance = 0;
      break;
    }
  }

  return {
    months: log,
    totalInterest: round2(totalInterest),
    endBalance: round2(balance),
    isPaidOff: balance <= 0.005,
    monthsToPayoff: paidOffMonth,
    shadowInterest: round2(shadowInterestTotal),
  };
}
